import { getResults, getSeasonRounds } from '../core/api';
import { SEASON_ID } from '../core/config';
import { useT } from '../core/lexicon';
import { ordinal, seq } from '../core/util';
import { useAsync } from '../core/useAsync';
import { useApp } from '../App';
import { Screen, TopBar, CenterNote } from './Layout';
import type { Results as ResultsData } from '../core/types';

/**
 * Last round, everything at once (spec §20's one deliberate divergence — no paced
 * reveal). Every song, its final score, who submitted it, whether you guessed
 * right, and any veto or bonus that applied. Scoring is identical to the other
 * styles; only the presentation differs.
 */
export function Results() {
  const t = useT();
  const { user } = useApp();

  const load = useAsync<{ res: ResultsData; sequence_no: number } | null>(async () => {
    const season = await getSeasonRounds(SEASON_ID);
    const closed = season.filter((r) => r.phase === 'CLOSED').sort((a, b) => b.sequence_no - a.sequence_no);
    if (closed.length === 0) return null;
    const res = await getResults(closed[0].round_id);
    return { res, sequence_no: closed[0].sequence_no };
  }, []);

  const title = t('phase.reveal.name');

  if (load.loading) {
    return (
      <Screen>
        <TopBar title={title} />
        <CenterNote>
          <div className="spin">LOADING…</div>
        </CenterNote>
      </Screen>
    );
  }
  if (load.error) {
    return (
      <Screen>
        <TopBar title={title} />
        <CenterNote title={t('empty.no_results').toUpperCase()}>
          <p className="errline">{load.error.message}</p>
        </CenterNote>
      </Screen>
    );
  }
  if (!load.data) {
    return (
      <Screen>
        <TopBar title={title} />
        <CenterNote title={t('empty.no_results').toUpperCase()} />
      </Screen>
    );
  }

  const res = load.data.res;
  const sequenceNo = load.data.sequence_no;
  const nameById = new Map(res.tracks.map((tr) => [tr.submitted_by.user_id, tr.submitted_by.display_name]));
  const others = Math.max(res.tracks.length - 1, 1);
  const you = res.you;

  return (
    <Screen>
      <TopBar title={title} sub={t('round.label', { n: sequenceNo }).toUpperCase()} right={t('state.shut').toUpperCase()} />
      <div className="theme">{res.theme.toUpperCase()}</div>

      <section className="mine">
        <h2>{t('phase.reveal.own').toUpperCase()}</h2>
        <div className="grid">
          <div className="cell">
            <div className="l">SCORED</div>
            <div className="v">{signed(you.track_points)}</div>
          </div>
          <div className="cell">
            <div className="l">{t('schedule.now').toUpperCase()}</div>
            <div className="v">{ordinal(you.placing).toUpperCase()}</div>
          </div>
          <div className="cell">
            <div className="l">{t('board.guess').toUpperCase()}</div>
            <div className="v">{you.informants}/{others}</div>
          </div>
          <div className="cell">
            <div className="l">{t('board.evade').toUpperCase()}</div>
            <div className="v">{you.ghosts}/{others}</div>
          </div>
        </div>
        {you.prediction ? (
          <p className="note" style={{ marginTop: 10 }}>
            {t('phase.predict.name')}: {you.prediction.hit ? `${t('phase.predict.hit')} +${you.prediction.bonus}` : t('phase.predict.miss')}
            {' · '}
            {you.prediction.title}
          </p>
        ) : null}
      </section>

      <div className="list">
        {res.tracks.map((tr) => {
          const mine = tr.submitted_by.user_id === user.user_id;
          const flags: { text: string; hit?: boolean }[] = [];

          if (tr.veto_applied) {
            flags.push({ text: `${t('phase.veto.applied')} · ${tr.track_points}/−${res.ruleset.veto_penalty}`.toUpperCase(), hit: true });
          } else if (tr.veto_count > 0) {
            flags.push({ text: t('phase.veto.nearmiss', { a: tr.veto_count, b: res.ruleset.veto_threshold - tr.veto_count }).toUpperCase() });
          }
          if (tr.popularity_bonus > 0) {
            flags.push({ text: `${t('phase.reveal.bonus')} · +${tr.popularity_bonus}`.toUpperCase(), hit: true });
          }
          if (mine) {
            flags.push({ text: t('phase.reveal.own').toUpperCase() });
          } else if (tr.your_guess) {
            flags.push(
              tr.your_guess.correct
                ? { text: t('phase.reveal.correct').toUpperCase(), hit: true }
                : { text: t('phase.reveal.wrong', { name: nameById.get(tr.your_guess.user_id) ?? '?' }).toUpperCase() },
            );
          }

          return (
            <div key={tr.submission_id} className={`rrow${tr.placing === 1 ? ' top1' : ''}${tr.veto_applied ? ' veto' : ''}`}>
              <div className="pos">{seq(tr.placing)}</div>
              <div>
                <div className="song">{tr.title}</div>
                <div className="by">
                  {(tr.artist ?? '') + ' · '}
                  {tr.submitted_by.display_name.toUpperCase()}
                </div>
                <div className="flags">
                  {flags.map((f, i) => (
                    <span key={i} className={`flag${f.hit ? ' hit' : ''}`}>
                      {f.text}
                    </span>
                  ))}
                </div>
              </div>
              <div className="score">{signed(tr.final_score)}</div>
            </div>
          );
        })}
      </div>
    </Screen>
  );
}

const signed = (n: number): string => (n > 0 ? '+' + n : String(n));
